import { createTheme, responsiveFontSizes, alpha } from "@mui/material/styles";

/**
 * ChikitsaLink — Material UI theme
 *
 * Palette direction: calm medical green with a warm amber accent
 * (mirrors the logo: Chikitsa = deep green, Link = amber). Designed
 * for clean, trustworthy healthcare UI.
 */
const base = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2E7D32",
      light: "#60AD5E",
      dark: "#1B5E20",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#00897B",
      light: "#4EBAAA",
      dark: "#005B4F",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#F57C00",
      light: "#FFAD42",
      dark: "#BB4D00",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#43A047",
      light: "#76D275",
      dark: "#00701A",
    },
    info: {
      main: "#0288D1",
    },
    error: {
      main: "#D32F2F",
    },
    background: {
      default: "#F6FBF7",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A2E22",
      secondary: "#4F6A58",
    },
    divider: alpha("#1B5E20", 0.12),
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily:
      '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 800, letterSpacing: "-1.5px" },
    h2: { fontWeight: 800, letterSpacing: "-1px" },
    h3: { fontWeight: 700, letterSpacing: "-0.5px" },
    h4: { fontWeight: 700, letterSpacing: "-0.25px" },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: "0.2px" },
    subtitle1: { fontWeight: 500 },
    body1: { fontSize: "1rem", lineHeight: 1.6 },
  },
});

const theme = responsiveFontSizes(
  createTheme(base, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: `radial-gradient(1200px 500px at -10% -20%, ${alpha(
              base.palette.primary.light,
              0.12,
            )} 0%, transparent 60%),
            radial-gradient(900px 400px at 110% 0%, ${alpha(
              base.palette.secondary.light,
              0.12,
            )} 0%, transparent 60%),
            ${base.palette.background.default}`,
            minHeight: "100vh",
          },
          "a": { color: base.palette.primary.dark, textDecoration: "none" },
          "a:hover": { textDecoration: "underline" },
          "::selection": {
            backgroundColor: alpha(base.palette.primary.main, 0.25),
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: "saturate(180%) blur(14px)",
            backgroundColor: alpha("#FFFFFF", 0.85),
            color: base.palette.text.primary,
            borderBottom: `1px solid ${base.palette.divider}`,
            boxShadow: "none",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 12, paddingInline: 18, paddingBlock: 10 },
          containedPrimary: {
            background: `linear-gradient(135deg, ${base.palette.primary.main} 0%, ${base.palette.primary.dark} 100%)`,
            "&:hover": {
              background: `linear-gradient(135deg, ${base.palette.primary.dark} 0%, ${base.palette.primary.dark} 100%)`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: { borderRadius: 16 },
          outlined: { borderColor: alpha(base.palette.primary.dark, 0.12) },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            border: `1px solid ${alpha(base.palette.primary.dark, 0.08)}`,
            boxShadow:
              "0 1px 2px rgba(16,44,26,0.04), 0 12px 30px -18px rgba(16,44,26,0.25)",
            transition: "transform .2s ease, box-shadow .2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                "0 2px 4px rgba(16,44,26,0.06), 0 20px 40px -18px rgba(16,44,26,0.35)",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
          colorPrimary: {
            backgroundColor: alpha(base.palette.primary.main, 0.12),
            color: base.palette.primary.dark,
          },
          colorSecondary: {
            backgroundColor: alpha(base.palette.secondary.main, 0.12),
            color: base.palette.secondary.dark,
          },
        },
      },
      MuiTextField: {
        defaultProps: { variant: "outlined", fullWidth: true },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
      MuiLink: {
        defaultProps: { underline: "hover" },
      },
    },
  }),
);

export default theme;
